import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { RootReduxState } from 'app/shared/flux/reducers/index';
import ListingEngineActions from 'app/shared/flux/actions/ListingEngineActions';
import AppActions from 'app/shared/flux/actions/AppActions';
import { getGlobalLogger } from '@zg-rentals/logger-base';
import {
  FullscreenOverlayBackdrop,
  FullscreenOverlayBody,
  FullscreenOverlayContent,
  FullscreenOverlayFooter,
  FullscreenOverlayHeader,
  FullscreenOverlayPortal,
  FullscreenOverlayRoot,
} from '../../../ui/FullscreenOverlay';
import { Button } from '../../../ui/Button';
import * as styles from './reportListingOverlay.styles';

const logger = getGlobalLogger('halo/ListingFeedbackModule');

const REPORT_REASONS = [
  { value: 'scam', label: 'Fraud' },
  { value: 'spam', label: 'Spam' },
  { value: 'offensive', label: 'Inappropriate content' },
  { value: 'fairHousing', label: 'Discriminatory or offensive listing' },
  { value: 'notAvailable', label: 'Not available' },
  { value: 'badDescription', label: 'Inaccurate' },
  { value: 'other', label: 'Other' },
] as const;

const REASONS_REQUIRING_COMMENT = new Set(['badDescription', 'fairHousing', 'offensive', 'other', 'spam']);

type ReportListingOverlayProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export const ReportListingOverlay = ({ open, onOpenChange }: ReportListingOverlayProps) => {
  const dispatch = useDispatch();
  const listing = useSelector((state: RootReduxState) => state.currentListingDetails?.currentListing);
  const [reason, setReason] = useState<string | null>(null);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const resetForm = () => {
    setReason(null);
    setMessage('');
    setLoading(false);
    setError(null);
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      resetForm();
    }
    onOpenChange(nextOpen);
  };

  const handleSubmit = async () => {
    if (!listing) return;

    if (!reason) {
      setError('Please select a reason for reporting this listing.');
      return;
    }

    if (REASONS_REQUIRING_COMMENT.has(reason) && message.length === 0) {
      setError('Please describe the problem with this listing.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await dispatch(
        ListingEngineActions.sendReport({
          alias: listing.aliasEncoded,
          type: reason,
          message,
        }),
      );
      dispatch(
        AppActions.toggleNotificationMessage({
          content: 'This listing has been reported. Thanks for helping keep HotPads safe.',
        }),
      );
      handleOpenChange(false);
    } catch (err) {
      const msg = 'Failed to report listing. Please try again.';
      logger?.warn(err, msg);
      setError(msg);
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <FullscreenOverlayRoot open={open} onOpenChange={handleOpenChange}>
      <FullscreenOverlayPortal>
        <FullscreenOverlayBackdrop>
          <FullscreenOverlayContent variant="standard">
            <FullscreenOverlayHeader title="Report this listing" />

            <FullscreenOverlayBody>
              <div className={styles.form}>
                <span className={styles.subtitle}>
                  Thanks for helping keep HotPads safe. Let us know what&apos;s going on with this listing.
                </span>

                <div className={styles.textareaWrapper}>
                  <label htmlFor="report-message" className={styles.label}>
                    Describe the issue
                  </label>
                  <textarea
                    id="report-message"
                    className={styles.textarea}
                    maxLength={750}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Tell us what you noticed..."
                  />
                </div>

                <fieldset style={{ border: 'none', margin: 0, padding: 0 }}>
                  <legend className={styles.label}>Reason for reporting</legend>
                  <div className={styles.radioGroup} role="radiogroup">
                    {REPORT_REASONS.map(({ value, label }) => (
                      <label key={value} className={styles.radioLabel}>
                        <input
                          type="radio"
                          name="report-reason"
                          value={value}
                          checked={reason === value}
                          onChange={() => setReason(value)}
                          className={styles.radioInput}
                        />
                        {label}
                      </label>
                    ))}
                  </div>
                </fieldset>

                {error && (
                  <div role="alert" className={styles.errorMessage}>
                    {error}
                  </div>
                )}
              </div>
            </FullscreenOverlayBody>

            <FullscreenOverlayFooter>
              <Button tone="brand" emphasis="filled" fluid loading={loading} onClick={handleSubmit}>
                {loading ? 'Reporting...' : 'Report this listing'}
              </Button>
            </FullscreenOverlayFooter>
          </FullscreenOverlayContent>
        </FullscreenOverlayBackdrop>
      </FullscreenOverlayPortal>
    </FullscreenOverlayRoot>
  );
};
